import { AlertCircle, X, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

const getErrorDetails = (message: string) => {
  if (message.includes('API key')) {
    return {
      title: 'API Key Missing',
      description: 'OpenAI API key is not configured. Please contact your administrator.',
      showDocs: true,
      docsUrl: '/admin'
    };
  }
  if (message.includes('rate limit')) {
    return {
      title: 'Rate Limit Exceeded',
      description: 'Too many requests. Please wait a moment and try again.',
      showDocs: false
    };
  }
  if (message.includes('network')) {
    return {
      title: 'Connection Error',
      description: 'Unable to connect to the AI service. Check your internet connection.',
      showDocs: false
    };
  }
  return {
    title: 'Something went wrong',
    description: message,
    showDocs: false
  };
};

export const ErrorMessage = ({ message, onRetry, onDismiss }: ErrorMessageProps) => {
  const errorDetails = getErrorDetails(message);

  return (
    <Alert variant="destructive" className="mx-4 my-2 border-destructive/50 bg-gradient-to-r from-destructive/10 to-destructive/5">
      <AlertCircle className="h-5 w-5" />
      <AlertTitle className="font-semibold">{errorDetails.title}</AlertTitle>
      <AlertDescription className="mt-2">
        <div className="flex flex-col gap-3">
          <span className="text-sm">{errorDetails.description}</span>
          <div className="flex items-center gap-2 flex-wrap">
            {onRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="h-8 px-3 bg-background hover:bg-background/80"
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Retry
              </Button>
            )}
            {errorDetails.showDocs && errorDetails.docsUrl && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-8 px-3 bg-background hover:bg-background/80"
              >
                <a href={errorDetails.docsUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1.5" />
                  Settings
                </a>
              </Button>
            )}
            {onDismiss && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="h-8 w-8 p-0 ml-auto"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
